import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Mail,
  Key,
  Eye,
  EyeOff,
  Home,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Lock,
  Shield,
  ArrowLeft
} from 'lucide-react';

function ForgotPassword() {
  const server_app_api_php = window.SERVER_APP_API_PHP;

  const EMAIL_REGEX = /^[^@]+@[^@]+\.[^@]{2,4}$/;

  // State variables
  const [OTP, setOTP] = useState('');
  const [validOTP, setValidOTP] = useState(false);
  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [validRepeatPassword, setValidRepeatPassword] = useState(false);
  const [repeatPasswordFocus, setRepeatPasswordFocus] = useState(false);
  
  // UI state
  const [sendEmailButton, setSendEmailButton] = useState(true);
  const [OTPButton, setOTPButton] = useState(false);
  const [handleSubmitButton, setHandleSubmitButton] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeStep, setActiveStep] = useState(1); // 1: Email, 2: OTP, 3: Password

  // Validation effects
  useEffect(() => {
    const result = EMAIL_REGEX.test(email);
    setValidEmail(result);
  }, [email]);

  useEffect(() => {
    if (newPassword === repeatPassword && newPassword.length > 0) {
      setValidRepeatPassword(true);
    } else {
      setValidRepeatPassword(false);
    }
  }, [repeatPassword, newPassword]);

  // Handle email submission
  const handleSubmitEmail = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('apiType', 'changePasswordGenerateOTP');
    formData.append('email', email);

    try {
      const response = await axios.post(
        `${server_app_api_php}/server/index.php`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data === "Email Is Correct") {
        setMessage({ type: 'success', text: 'An OTP has been sent to your email.' });
        setOTPButton(true);
        setSendEmailButton(false);
        setActiveStep(2);
      } else {
        setMessage({ type: 'error', text: response.data });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error: Check your internet connection' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP submission
  const handleSubmitOTP = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('apiType', 'changePasswordGenerateComfirmOTP');
    formData.append('email', email);
    formData.append('OTP', OTP);

    try {
      const response = await axios.post(
        `${server_app_api_php}/server/index.php`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data === "Correct OTP") {
        setMessage({ type: 'success', text: 'OTP verified! Please create a new password.' });
        setValidOTP(true);
        setOTPButton(false);
        setSendEmailButton(false);
        setHandleSubmitButton(true);
        setActiveStep(3);
      } else {
        setMessage({ type: 'error', text: response.data });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error: Check your internet connection' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password change
  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('apiType', 'ChangePassword');
    formData.append('email', email);
    formData.append('password', newPassword);

    try {
      const response = await axios.post(
        `${server_app_api_php}/server/index.php`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setMessage({ type: 'success', text: response.data });
      
      if (response.data === "Password Changed Successfully") {
        setHandleSubmitButton(false);
        setSendEmailButton(true);
        setEmail('');
        setOTP('');
        setNewPassword('');
        setRepeatPassword('');
        setActiveStep(1);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error: Check your internet connection' });
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setValidOTP(false);
    setValidEmail(false);
    setValidRepeatPassword(false);
    setRepeatPasswordFocus(false);
    setSendEmailButton(true);
    setOTPButton(false);
    setHandleSubmitButton(false);
    setEmail('');
    setOTP('');
    setNewPassword('');
    setRepeatPassword('');
    setMessage({ type: '', text: '' });
    setActiveStep(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
            <Key className="h-6 w-6 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Reset Your Password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Follow these steps to reset your password securely
        </p>
      </div>

      {/* Main Card */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-4xl">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        activeStep >= step
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      } font-semibold`}
                    >
                      {step}
                    </div>
                    <span className="mt-2 text-xs font-medium text-gray-600">
                      {step === 1 && 'Enter Email'}
                      {step === 2 && 'Verify OTP'}
                      {step === 3 && 'New Password'}
                    </span>
                  </div>
                  {step < 3 && (
                    <div className="flex-1 h-1 bg-gray-200 mx-4 relative">
                      <div
                        className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-300 absolute left-0 top-0"
                        style={{ 
                          width: step === 1 && activeStep >= 2 ? '100%' : 
                                 step === 2 && activeStep >= 3 ? '100%' : '0%' 
                        }}
                      ></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center mb-6">
            <a
              href="."
              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Home className="h-4 w-4 mr-1" />
              Back to Home
            </a>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Reset Process
            </button>
          </div>

          {/* Message Display */}
          {message.text && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                message.type === 'error'
                  ? 'bg-red-50 border border-red-200 text-red-700'
                  : 'bg-green-50 border border-green-200 text-green-700'
              }`}
            >
              <div className="flex items-start">
                {message.type === 'error' ? (
                  <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                )}
                <span>{message.text}</span>
              </div>
            </div>
          )}

          {/* Step 1: Email Form */}
          {activeStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Enter your email address</h3>
                <p className="mt-1 text-sm text-gray-600">
                  We'll send a verification code to this email
                </p>
              </div>

              <form onSubmit={handleSubmitEmail} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-200"
                      placeholder="you@example.com"
                    />
                    {email && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {validEmail ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {email && !validEmail && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Please enter a valid email address
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!validEmail || isLoading}
                  className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ${
                    !validEmail || isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending OTP...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {activeStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Verify your email</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Enter the 6-digit code sent to <span className="font-semibold">{email}</span>
                </p>
              </div>

              <form onSubmit={handleSubmitOTP} className="space-y-6">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    Verification Code
                  </label>
                  <div className="mt-1">
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      value={OTP}
                      onChange={(e) => setOTP(e.target.value)}
                      className="block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-200 text-center tracking-widest"
                      placeholder="123456"
                      maxLength="6"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Didn't receive the code? Check your spam folder or{' '}
                    <button
                      type="button"
                      onClick={handleSubmitEmail}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      resend
                    </button>
                  </p>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveStep(1);
                      setOTPButton(false);
                      setSendEmailButton(true);
                    }}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 inline-flex items-center justify-center"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={OTP.length !== 6 || isLoading}
                    className={`flex-1 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 inline-flex items-center justify-center ${
                      OTP.length !== 6 || isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Verifying...
                      </>
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: New Password */}
          {activeStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Create new password</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Your new password must be different from previously used passwords
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showPasswordNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-200"
                      placeholder="Enter new password"
                      required
                      minLength="6"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordNew(!showPasswordNew)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPasswordNew ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="repeatPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="repeatPassword"
                      name="repeatPassword"
                      type={showPasswordRepeat ? 'text' : 'password'}
                      value={repeatPassword}
                      onChange={(e) => setRepeatPassword(e.target.value)}
                      onFocus={() => setRepeatPasswordFocus(true)}
                      onBlur={() => setRepeatPasswordFocus(false)}
                      className="pl-10 pr-10 block w-full px-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-200"
                      placeholder="Confirm new password"
                      required
                      minLength="6"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPasswordRepeat ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {repeatPasswordFocus && !validRepeatPassword && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      Passwords do not match
                    </p>
                  )}
                  {validRepeatPassword && (
                    <p className="mt-2 text-sm text-green-600 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Passwords match
                    </p>
                  )}
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveStep(2);
                      setHandleSubmitButton(false);
                      setOTPButton(true);
                    }}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 inline-flex items-center justify-center"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!validRepeatPassword || isLoading}
                    className={`flex-1 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 inline-flex items-center justify-center ${
                      !validRepeatPassword || isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center mb-2">
              <Shield className="h-4 w-4 text-gray-600 mr-2" />
              <h4 className="text-sm font-medium text-gray-900">Security Tips:</h4>
            </div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li className="flex items-start">
                <CheckCircle className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                Use a strong password with at least 8 characters
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                Include numbers, letters, and special characters
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                Never share your password with anyone
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500">
          Having trouble? Contact our{' '}
          <a href="/support" className="font-medium text-blue-600 hover:text-blue-800 transition-colors">
            support team
          </a>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;